import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Input, message, Popconfirm, Tag, Space } from 'antd';
import { PlusOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { cardAPI } from '../../utils/api';

const { TextArea } = Input;

const Cards = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [batchText, setBatchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    loadCards();
  }, [productId]);

  const loadCards = async () => {
    try {
      setLoading(true);
      const cards = await cardAPI.getByProduct(productId, false);
      setCards(cards);
    } catch (error) {
      message.error('加载卡密失败');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchAdd = async () => {
    if (!batchText.trim()) {
      message.error('请输入卡密信息');
      return;
    }

    try {
      const lines = batchText.trim().split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        message.error('没有有效的卡密数据');
        return;
      }

      // 批量创建卡密
      const results = await Promise.allSettled(
        lines.map(line => {
          const cardNumber = line.trim();
          return cardAPI.create({
            productId: parseInt(productId),
            cardNumber,
            cardPassword: '',
            status: 'available'
          });
        })
      );

      // 调试：输出结果
      console.log('添加卡密结果:', results);
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.error(`第${i+1}条失败:`, r.reason);
        }
      });

      const successCount = results.filter(r => r.status === 'fulfilled').length;
      
      // 更新商品库存（增加添加的卡密数量）
      if (successCount > 0) {
        try {
          // 获取当前商品信息
          const products = await cardAPI.getProductInfo(productId);
          if (products && products.length > 0) {
            const product = products[0];
            // 更新库存
            await cardAPI.updateProductStock(productId, product.stock + successCount);
          }
        } catch (err) {
          console.error('更新库存失败:', err);
        }
      }
      
      if (successCount > 0) {
        message.success(`成功添加 ${successCount} 张卡密`);
      } else {
        message.error('添加失败，请查看控制台错误信息');
      }
      
      setModalVisible(false);
      setBatchText('');
      loadCards();
    } catch (error) {
      console.error('批量添加异常:', error);
      message.error(error.message || '添加失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await cardAPI.delete(id);
      
      // 更新商品库存（减少1）
      try {
        const products = await cardAPI.getProductInfo(productId);
        if (products && products.length > 0) {
          const product = products[0];
          await cardAPI.updateProductStock(productId, Math.max(0, product.stock - 1));
        }
      } catch (err) {
        console.error('更新库存失败:', err);
      }
      
      message.success('删除成功');
      loadCards();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的卡密');
      return;
    }

    try {
      // 批量删除
      await Promise.all(selectedRowKeys.map(id => cardAPI.delete(id)));
      
      // 更新商品库存（减少删除的数量）
      try {
        const products = await cardAPI.getProductInfo(productId);
        if (products && products.length > 0) {
          const product = products[0];
          await cardAPI.updateProductStock(productId, Math.max(0, product.stock - selectedRowKeys.length));
        }
      } catch (err) {
        console.error('更新库存失败:', err);
      }
      
      message.success(`成功删除 ${selectedRowKeys.length} 张卡密`);
      setSelectedRowKeys([]);
      loadCards();
    } catch (error) {
      message.error('批量删除失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60
    },
    {
      title: '卡号',
      dataIndex: 'cardNumber',
      key: 'cardNumber'
    },
    {
      title: '卡密',
      dataIndex: 'cardPassword',
      key: 'cardPassword',
      render: (password) => password || '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => (
        <Tag color={status === 'available' ? 'success' : 'default'}>
          {status === 'available' ? '可用' : status === 'sold' ? '已售' : '已用'}
        </Tag>
      )
    },
    {
      title: '订单ID',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 80,
      render: (orderId) => orderId || '-'
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (time) => new Date(time).toLocaleString('zh-CN')
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record) => (
        record.status === 'available' && (
          <Popconfirm
            title="确定删除此卡密吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        )
      )
    }
  ];

  return (
    <div>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/admin/products')}
        style={{ marginBottom: 16 }}
      >
        返回商品列表
      </Button>

      <Card
        title={`卡密管理 - 商品ID: ${productId}`}
        extra={
          <Space>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBatchDelete}
              disabled={selectedRowKeys.length === 0}
            >
              批量删除 {selectedRowKeys.length > 0 && `(${selectedRowKeys.length})`}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setModalVisible(true)}
            >
              批量添加
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={cards}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1000 }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            getCheckboxProps: (record) => ({
              disabled: record.status !== 'available'
            })
          }}
        />
      </Card>

      <Modal
        title="批量添加卡密"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleBatchAdd}
        width={700}
      >
        <p>每行一个激活码</p>
        <p style={{ color: '#666', fontSize: 12 }}>示例：</p>
        <pre style={{ background: '#f5f5f5', padding: 8, fontSize: 12 }}>
{`CARD-2024-001-ABC
CARD-2024-002-DEF
CARD-2024-003-XYZ`}
        </pre>
        <TextArea
          rows={10}
          value={batchText}
          onChange={(e) => setBatchText(e.target.value)}
          placeholder="请输入激活码，每行一个..."
        />
      </Modal>
    </div>
  );
};

export default Cards;
